import { Stack } from '@mui/material';
import { sortBy } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { TypeaheadSelectInput } from '@/components/form/baseInputs/TypeaheadSelectInput/TypeaheadSelectInput';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import {
  useFetchAiSuggestionDuplicateTargetsQuery,
  useMarkSuggestionAsDuplicateMutation,
} from '@/modules/aiSuggestionReviewCard/graphql/aiSuggestionReviewCard.generated';
import { FetchAiSuggestionsForReviewCardV2Document } from '@/modules/aiSuggestionReviewCard/graphql/aiSuggestionReviewCardV2.generated';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import {
  BUSINESS_ENTITY_KINDS,
  NON_TRUST_CHARITABLE_ENTITY_KINDS,
  TRUST_ENTITY_KINDS,
} from '@/modules/entities/entities.constants';
import { formatCreatedFromEntities } from '@/modules/entities/testamentaryEntities/testamentaryEntities.utils';
import { useRequiredHouseholdId } from '@/modules/household/contexts/useRequiredHouseholdId';
import {
  EntityKind,
  EntityStage,
  MarkSuggestionAsDuplicateInput,
} from '@/types/schema';

import {
  DuplicateTargetKind,
  getDuplicateTargetLabel,
  getDuplicateTargetPluralLabel,
} from './MarkSuggestionAsDuplicate.utils';

interface MarkSuggestionAsDuplicateModalProps {
  suggestion: AiSuggestionForReviewCard;
  targetKind: DuplicateTargetKind;
  onClose: () => void;
  onMarkedDuplicate: (suggestionID: string) => void;
  beginOperation?: () => boolean;
  endOperation?: () => void;
}

export function MarkSuggestionAsDuplicateModal({
  suggestion,
  targetKind,
  onClose,
  onMarkedDuplicate,
  beginOperation,
  endOperation,
}: MarkSuggestionAsDuplicateModalProps) {
  const householdId = useRequiredHouseholdId();
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();
  const trackUserEvent = useTrackUserEvent();
  const [selectedTargetID, setSelectedTargetID] = useState<string | null>(null);
  const mutationInFlightRef = useRef(false);

  const { data, loading: targetsLoading } =
    useFetchAiSuggestionDuplicateTargetsQuery({
      variables: { householdID: householdId },
      fetchPolicy: 'cache-and-network',
      onError: (error) => {
        reportError('Failed to load duplicate suggestion targets', error);
        showFeedback('Failed to load existing records. Please try again.');
      },
    });
  const [markAsDuplicate, { loading: mutationLoading }] =
    useMarkSuggestionAsDuplicateMutation();

  const options = useMemo(() => {
    if (targetKind === 'subtrust') {
      const subtrusts = (data?.testamentaryEntities.edges ?? []).flatMap(
        (edge) => (edge?.node ? [edge.node] : [])
      );
      return sortBy(
        subtrusts.map((subtrust) => ({
          display: subtrust.displayName,
          value: subtrust.id,
          secondaryText:
            formatCreatedFromEntities(subtrust.createdFromEntities) ??
            undefined,
        })),
        'display'
      );
    }

    const clientOrganizations = (
      data?.clientOrganizations?.edges ?? []
    ).flatMap((edge) => (edge?.node ? [edge.node] : []));
    let allowedKinds: Readonly<EntityKind[]> = TRUST_ENTITY_KINDS;
    if (targetKind === 'business') {
      allowedKinds = BUSINESS_ENTITY_KINDS;
    } else if (targetKind === 'charitableVehicle') {
      allowedKinds = NON_TRUST_CHARITABLE_ENTITY_KINDS;
    }
    const clientOrganizationOptions =
      targetKind === 'charitableVehicle'
        ? clientOrganizations.map((organization) => ({
            display: organization.name,
            value: organization.id,
          }))
        : [];
    return sortBy(
      [
        ...(data?.entities.edges ?? [])
          .flatMap((edge) => (edge?.node ? [edge.node] : []))
          .filter(
            (entity) =>
              entity.stage === EntityStage.Active &&
              allowedKinds.includes(entity.kind)
          )
          .map((entity) => ({
            display: entity.subtype.displayName,
            value: entity.id,
          })),
        ...clientOrganizationOptions,
      ],
      'display'
    );
  }, [
    data?.clientOrganizations?.edges,
    data?.entities.edges,
    data?.testamentaryEntities.edges,
    targetKind,
  ]);

  const targetLabel = getDuplicateTargetLabel(targetKind);
  const noTargetsAvailable = !targetsLoading && options.length === 0;
  const noTargetsHelpText = noTargetsAvailable
    ? `No existing ${getDuplicateTargetPluralLabel(targetKind)} are available.`
    : undefined;

  const handleClose = useCallback(() => {
    if (!mutationInFlightRef.current) {
      onClose();
    }
  }, [onClose]);

  const handleConfirm = async () => {
    if (
      !selectedTargetID ||
      mutationInFlightRef.current ||
      beginOperation?.() === false
    ) {
      return;
    }

    mutationInFlightRef.current = true;

    try {
      trackUserEvent('ai_suggestion mark as duplicate', {
        id: suggestion.id,
        kind: suggestion.kind,
      });

      const targetsClientOrganization =
        targetKind === 'charitableVehicle' &&
        (data?.clientOrganizations?.edges ?? []).some(
          (edge) => edge?.node?.id === selectedTargetID
        );
      const input: MarkSuggestionAsDuplicateInput = {
        aiSuggestionID: suggestion.id,
      };
      if (targetKind === 'subtrust') {
        input.testamentaryEntityID = selectedTargetID;
      } else if (targetsClientOrganization) {
        input.clientOrganizationID = selectedTargetID;
      } else {
        input.entityID = selectedTargetID;
      }
      await markAsDuplicate({
        variables: {
          input,
        },
        refetchQueries: [FetchAiSuggestionsForReviewCardV2Document],
        awaitRefetchQueries: true,
      });
      onMarkedDuplicate(suggestion.id);
      showFeedback('Suggestion marked as duplicate', { variant: 'success' });
      onClose();
    } catch (error) {
      reportError(
        'Failed to mark suggestion as duplicate',
        error instanceof Error ? error : new Error(String(error))
      );
      showFeedback('Failed to mark suggestion as duplicate. Please try again.');
    } finally {
      mutationInFlightRef.current = false;
      endOperation?.();
    }
  };

  return (
    <DialogModal
      isOpen
      heading="Mark as duplicate"
      subheading={`Select the existing ${targetLabel} that matches this suggestion.`}
      onClose={handleClose}
      showCloseButton={!mutationLoading}
      actions={
        <Button
          size="sm"
          variant="primary"
          onClick={handleConfirm}
          disabled={!selectedTargetID || targetsLoading}
          loading={mutationLoading}
        >
          Mark as duplicate
        </Button>
      }
    >
      <Stack gap={1}>
        <TypeaheadSelectInput<string>
          id="duplicate-suggestion-target"
          label={`Existing ${targetLabel}`}
          hideLabel
          placeholder={`Select a ${targetLabel}`}
          helpText={noTargetsHelpText}
          options={options}
          value={selectedTargetID ?? ''}
          onChange={(_event, value) => setSelectedTargetID(value || null)}
          disabled={targetsLoading || mutationLoading || noTargetsAvailable}
          required
        />
      </Stack>
    </DialogModal>
  );
}
